import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { useSupabase } from '@/hooks/useSupabase'
import { trpc } from '@/trpc/react'
import {
    bindPushSubscriptionToUser,
    enablePushNotifications,
    isPushHealthy,
    readPushStateAsync,
    readPushStateSync,
    repairPushRegistrationIfNeeded,
    subscribeToPushStateChanges,
    waitForHealthyPushState,
    type PushState,
} from '@/lib/onesignal-push'

export type ServerRegistrationStatus =
    | 'idle'
    | 'registered'
    | 'not_registered'
    | 'not_configured'
    | 'auth_error'
    | 'unavailable'

export interface PushRegistrationSnapshot {
    localState: PushState
    serverStatus: ServerRegistrationStatus
    locallyHealthy: boolean
    isFullyHealthy: boolean
    isHealing: boolean
    isVerifyingServer: boolean
    lastHealedAt: number | null
}

const RETRY_INTERVAL_MS = 30_000
const DISPLAY_SYNC_MS = 1_000
const HEAL_DEBOUNCE_MS = 1_500

function pushStatesEqual(a: PushState, b: PushState): boolean {
    return a.permission === b.permission
        && a.optedIn === b.optedIn
        && a.subscriptionId === b.subscriptionId
        && a.pushToken === b.pushToken
        && a.externalId === b.externalId
}

function isConfirmedServerError(status: ServerRegistrationStatus): boolean {
    return status === 'not_registered'
        || status === 'not_configured'
        || status === 'auth_error'
        || status === 'unavailable'
}

function deviceHasCredentials(state: PushState): boolean {
    return Boolean(state.subscriptionId && state.pushToken)
}

async function verifyOnServer(
    fetchVerify: (input: { subscriptionId?: string; pushToken?: string }) => Promise<{
        configured: boolean
        registered: boolean
        authError?: boolean
    }>,
    state: PushState,
): Promise<ServerRegistrationStatus> {
    if (!deviceHasCredentials(state)) {
        return 'not_registered'
    }

    const result = await fetchVerify({
        subscriptionId: state.subscriptionId ?? undefined,
        pushToken: state.pushToken ?? undefined,
    })

    if (!result.configured) return 'not_configured'
    if (result.authError) return 'auth_error'
    if (result.registered) return 'registered'
    return 'not_registered'
}

function deriveSnapshot(
    localState: PushState,
    serverStatus: ServerRegistrationStatus,
    isHealing: boolean,
    lastHealedAt: number | null,
    userId?: string | null,
): PushRegistrationSnapshot {
    const locallyHealthy = isPushHealthy(localState, userId)
    const deviceRegisteredOnServer = serverStatus === 'registered' && deviceHasCredentials(localState)
    const isFullyHealthy = locallyHealthy && deviceRegisteredOnServer

    return {
        localState,
        serverStatus,
        locallyHealthy,
        isFullyHealthy,
        isHealing,
        isVerifyingServer: false,
        lastHealedAt,
    }
}

export function usePushSelfHeal(): PushRegistrationSnapshot & {
    heal: (options?: { requestPermission?: boolean; forceServerSync?: boolean }) => Promise<PushState | null>
} {
    const { user } = useSupabase()
    const utils = trpc.useUtils()
    const syncMutation = trpc.notification.syncPushRegistration.useMutation()
    const syncRef = useRef(syncMutation.mutateAsync)
    syncRef.current = syncMutation.mutateAsync

    const userId = user?.id

    // Lazy initializer — readPushStateSync() runs once on mount, not every render.
    const [localState, setLocalState] = useState<PushState>(readPushStateSync)
    const [serverStatus, setServerStatus] = useState<ServerRegistrationStatus>('idle')
    const [isHealing, setIsHealing] = useState(false)
    const [lastHealedAt, setLastHealedAt] = useState<number | null>(null)

    const inFlightRef = useRef(false)
    const unbindRef = useRef<(() => void) | null>(null)
    const healTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const displaySyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const liveLocalRef = useRef<PushState>(localState)
    const serverStatusRef = useRef<ServerRegistrationStatus>('idle')
    const displayLocalHealthyRef = useRef(false)
    const suppressUnhealthyDisplayRef = useRef(false)

    const applyLocalState = useCallback((next: PushState) => {
        liveLocalRef.current = next
        const nextHealthy = isPushHealthy(next, userId)

        if (!next.permission || !deviceHasCredentials(next)) {
            suppressUnhealthyDisplayRef.current = false
        }

        if (!nextHealthy) {
            displayLocalHealthyRef.current = false
        }

        if (suppressUnhealthyDisplayRef.current && displayLocalHealthyRef.current && !nextHealthy) {
            return
        }

        if (nextHealthy) {
            displayLocalHealthyRef.current = true
        }

        setLocalState((prev) => (pushStatesEqual(prev, next) ? prev : next))
    }, [userId])

    const commitServerStatus = useCallback((next: ServerRegistrationStatus) => {
        serverStatusRef.current = next

        if (next === 'registered') {
            suppressUnhealthyDisplayRef.current = true
        } else if (isConfirmedServerError(next)) {
            suppressUnhealthyDisplayRef.current = false
        }

        setServerStatus((prev) => (prev === next ? prev : next))
    }, [])

    const scheduleDisplaySync = useCallback(() => {
        if (displaySyncTimerRef.current) {
            clearTimeout(displaySyncTimerRef.current)
        }

        displaySyncTimerRef.current = setTimeout(() => {
            displaySyncTimerRef.current = null
            applyLocalState(liveLocalRef.current)
        }, DISPLAY_SYNC_MS)
    }, [applyLocalState])

    const heal = useCallback(async (options?: { requestPermission?: boolean; forceServerSync?: boolean }) => {
        if (Platform.OS === 'web' || !userId || inFlightRef.current) return null

        const showProgress = Boolean(options?.requestPermission || options?.forceServerSync)
        inFlightRef.current = true

        if (showProgress) {
            suppressUnhealthyDisplayRef.current = false
            setIsHealing(true)
        }

        try {
            let state = options?.requestPermission
                ? await enablePushNotifications(userId)
                : (await repairPushRegistrationIfNeeded(userId)) ?? await readPushStateAsync()

            if (state.permission && !deviceHasCredentials(state)) {
                state = await enablePushNotifications(userId)
            }

            if (state.permission && !isPushHealthy(state, userId)) {
                state = await waitForHealthyPushState(userId, showProgress ? 25_000 : 15_000)
            }

            applyLocalState(state)

            if (!state.permission) {
                commitServerStatus('idle')
                setLastHealedAt((prev) => prev ?? Date.now())
                return state
            }

            if (!deviceHasCredentials(state)) {
                commitServerStatus('not_registered')
                setLastHealedAt(Date.now())
                return state
            }

            let status = await verifyOnServer(
                (input) => utils.notification.verifyPushRegistration.fetch(input),
                state,
            )

            const shouldSync = options?.forceServerSync
                || (status !== 'registered' && status !== 'not_configured' && status !== 'auth_error')

            if (shouldSync) {
                if (showProgress) {
                    setIsHealing(true)
                }

                await syncRef.current({
                    pushToken: state.pushToken!,
                    platform: Platform.OS === 'ios' ? 'ios' : 'android',
                    subscriptionId: state.subscriptionId ?? undefined,
                })

                status = await verifyOnServer(
                    (input) => utils.notification.verifyPushRegistration.fetch(input),
                    state,
                )
            }

            commitServerStatus(status)
            setLastHealedAt(Date.now())

            return state
        } catch {
            commitServerStatus('unavailable')
            setLastHealedAt((prev) => prev ?? Date.now())
            return null
        } finally {
            inFlightRef.current = false
            if (showProgress) {
                setIsHealing(false)
            }
        }
    }, [applyLocalState, commitServerStatus, userId, utils.notification.verifyPushRegistration])

    const scheduleHeal = useCallback((options?: { requestPermission?: boolean; forceServerSync?: boolean }) => {
        if (healTimerRef.current) {
            clearTimeout(healTimerRef.current)
        }

        healTimerRef.current = setTimeout(() => {
            healTimerRef.current = null
            void heal(options)
        }, HEAL_DEBOUNCE_MS)
    }, [heal])

    const needsHeal = useCallback(() => {
        const local = liveLocalRef.current
        if (!isPushHealthy(local, userId)) return true
        if (!deviceHasCredentials(local)) return true
        if (serverStatusRef.current !== 'registered') return true
        return false
    }, [userId])

    useEffect(() => {
        if (Platform.OS === 'web' || !userId) return

        unbindRef.current?.()
        unbindRef.current = bindPushSubscriptionToUser(userId)

        const unsubscribe = subscribeToPushStateChanges((next) => {
            liveLocalRef.current = next
            scheduleDisplaySync()
            if (needsHeal()) {
                scheduleHeal()
            }
        })

        void readPushStateAsync().then(applyLocalState)
        void heal()

        const appStateSub = AppState.addEventListener('change', (nextState) => {
            if (nextState === 'active' && needsHeal()) {
                scheduleHeal()
            }
        })

        const retryInterval = setInterval(() => {
            if (inFlightRef.current || !needsHeal()) return
            scheduleHeal()
        }, RETRY_INTERVAL_MS)

        return () => {
            if (healTimerRef.current) {
                clearTimeout(healTimerRef.current)
                healTimerRef.current = null
            }
            if (displaySyncTimerRef.current) {
                clearTimeout(displaySyncTimerRef.current)
                displaySyncTimerRef.current = null
            }
            unsubscribe()
            unbindRef.current?.()
            unbindRef.current = null
            appStateSub.remove()
            clearInterval(retryInterval)
        }
    }, [applyLocalState, heal, needsHeal, scheduleDisplaySync, scheduleHeal, userId])

    const snapshot = useMemo(
        () => deriveSnapshot(localState, serverStatus, isHealing, lastHealedAt, userId),
        [isHealing, lastHealedAt, localState, serverStatus, userId],
    )

    return useMemo(
        () => ({
            ...snapshot,
            heal,
        }),
        [heal, snapshot],
    )
}
